import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboardEvents } from '../contexts/EventContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { DB_STATUS } from '../utils/eventStatusMapping';

const { 
  FiCalendar, 
  FiUsers, 
  FiTarget, 
  FiTrendingUp, 
  FiArrowRight,
  FiMail,
  FiPhone,
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiInstagram,
  FiAward,
  FiCheckCircle,
  FiZap,
  FiBarChart,
  FiGlobe
} = FiIcons;

const Home = () => {
  const { events } = useDashboardEvents();
  // Show events that are published (status = 'public' in DB)
  const publicEvents = events.filter(event => event.status === DB_STATUS.PUBLIC);

  const howItWorksSteps = [
    {
      icon: FiTarget,
      title: 'Create Events',
      description: 'Design professional learning experiences with customizable landing pages, seamless registration, and integrated payment solutions.',
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: FiUsers,
      title: 'Engage Participants',
      description: 'Connect with your audience through dynamic content delivery, interactive sessions, and real-time engagement tools.',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: FiTrendingUp,
      title: 'Drive Results',
      description: 'Track performance metrics, analyze engagement data, and convert leads into lasting professional relationships.',
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-violet-50'
    }
  ];

  const features = [
    { icon: FiZap, text: 'Instant Event Setup' },
    { icon: FiBarChart, text: 'Advanced Analytics' },
    { icon: FiAward, text: 'Professional Templates' },
    { icon: FiGlobe, text: 'Global Reach' }
  ];

  const stats = [
    { value: '10K+', label: 'Active Users' },
    { value: '500+', label: 'Events Hosted' },
    { value: '98%', label: 'Satisfaction Rate' },
    { value: '50+', label: 'Countries' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00AAFF] via-[#0088dd] to-[#1B4C87] rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">PL</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <span className="text-xl font-bold text-slate-800 block leading-none" style={{ fontFamily: 'Dancing Script, cursive' }}>
                    Prosperity Leaders
                  </span>
                  <span className="text-xs text-slate-500 font-medium">Events Platform</span>
                </div>
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-slate-700 hover:text-[#00AAFF] transition-colors font-medium text-sm">
                Home
              </Link>
              <a href="#about" className="text-slate-700 hover:text-[#00AAFF] transition-colors font-medium text-sm">
                About
              </a>
              <a href="#events" className="text-slate-700 hover:text-[#00AAFF] transition-colors font-medium text-sm">
                Events
              </a>
              <a href="#contact" className="text-slate-700 hover:text-[#00AAFF] transition-colors font-medium text-sm">
                Contact
              </a>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="bg-gradient-to-r from-[#00AAFF] to-[#0088dd] text-white px-6 py-2.5 rounded-lg hover:shadow-lg hover:scale-105 transition-all font-medium text-sm"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00AAFF] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-violet-500 rounded-full blur-3xl"></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41Ii8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6"
              >
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-white text-sm font-medium">Professional Event Management</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
              >
                Transform Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00AAFF] via-emerald-400 to-violet-400">
                  Event Experience
                </span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-slate-300 mb-8 leading-relaxed max-w-xl"
              >
                Empower your organization with enterprise-grade event management. Create, manage, and scale professional learning experiences that drive real business results.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8"
              >
                <Link
                  to="/login"
                  className="group bg-gradient-to-r from-[#00AAFF] to-[#0088dd] text-white px-8 py-4 rounded-xl hover:shadow-2xl hover:shadow-[#00AAFF]/50 transition-all font-semibold text-lg flex items-center space-x-2"
                >
                  <span>Get Started Free</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#events"
                  className="border-2 border-white/30 text-white px-8 py-4 rounded-xl hover:bg-white/10 backdrop-blur-sm transition-all font-semibold text-lg"
                >
                  Explore Events
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="grid grid-cols-2 gap-4"
              >
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-slate-300">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right Content - Stats Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all"
                  >
                    <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-slate-300 text-sm font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="rgb(248, 250, 252)"/>
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-50 py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:hidden">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-slate-800 mb-1">{stat.value}</div>
                <div className="text-slate-600 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section id="events" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-gradient-to-r from-[#00AAFF] to-emerald-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Upcoming Events
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Featured Learning Experiences
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Join industry-leading events designed to accelerate your professional growth and expand your network
              </p>
            </motion.div>
          </div>

          {publicEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {publicEvents.slice(0, 6).map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all overflow-hidden border border-gray-100"
                >
                  <div className="relative h-48 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00AAFF]/20 to-emerald-500/20"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <SafeIcon icon={FiCalendar} className="w-20 h-20 text-white/30 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5">
                      <span className="text-xs font-semibold text-slate-700">{event.price > 0 ? `$${event.price}` : 'Free'}</span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Live Event</span>
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#00AAFF] transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-2 text-sm leading-relaxed">
                      {event.description}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-5 pb-5 border-b border-gray-100">
                      <div className="flex items-center space-x-1.5">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 text-[#00AAFF]" />
                        <span className="font-medium">{event.date}</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <SafeIcon icon={FiUsers} className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium">{event.registrations} joined</span>
                      </div>
                    </div>

                    <Link
                      to={`/landing/${event.id}`}
                      className="group/btn w-full bg-gradient-to-r from-slate-800 to-slate-700 text-white py-3 px-4 rounded-xl hover:from-[#00AAFF] hover:to-[#0088dd] transition-all font-semibold flex items-center justify-center space-x-2 text-sm"
                    >
                      <span>View Details</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <SafeIcon icon={FiCalendar} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-slate-600 font-semibold">No events available at the moment</p>
              <p className="text-slate-500 mt-2">Check back soon for upcoming events!</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                How It Works
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Enterprise Event Management
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00AAFF] to-emerald-500 mt-2">
                  Made Simple
                </span>
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                A comprehensive platform that streamlines every aspect of event creation, management, and engagement
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className={`${step.bgColor} rounded-2xl p-8 h-full border-2 border-transparent group-hover:border-[#00AAFF] transition-all`}>
                  <div className="flex items-center justify-center mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <SafeIcon icon={step.icon} className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="text-center mb-4">
                    <div className="inline-block bg-white rounded-full px-4 py-1 mb-3">
                      <span className="text-sm font-bold text-slate-700">Step {index + 1}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {step.title}
                    </h3>
                  </div>
                  
                  <p className="text-slate-600 leading-relaxed text-center">
                    {step.description}
                  </p>
                </div>

                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <SafeIcon icon={FiArrowRight} className="w-8 h-8 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00AAFF] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <SafeIcon icon={FiZap} className="w-4 h-4 text-emerald-400" />
              <span className="text-white text-sm font-medium">Join 10,000+ Professionals</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Ready to Elevate Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00AAFF] to-emerald-400 mt-2">
                Event Strategy?
              </span>
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Start creating professional events today. No credit card required. Full access to all features.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#events"
                className="group bg-gradient-to-r from-[#00AAFF] to-[#0088dd] text-white px-10 py-4 rounded-xl hover:shadow-2xl hover:shadow-[#00AAFF]/50 transition-all font-bold text-lg flex items-center space-x-2"
              >
                <span>Browse Events</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/login"
                className="border-2 border-white/30 text-white px-10 py-4 rounded-xl hover:bg-white/10 backdrop-blur-sm transition-all font-bold text-lg"
              >
                Get Started Free
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00AAFF] to-emerald-400 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">PL</span>
                </div>
                <div>
                  <span className="text-2xl font-bold block leading-none" style={{ fontFamily: 'Dancing Script, cursive' }}>
                    Prosperity Leaders
                  </span>
                  <span className="text-xs text-slate-400">Events Platform</span>
                </div>
              </div>
              <p className="text-slate-400 mb-6 leading-relaxed max-w-md">
                Empowering organizations worldwide with enterprise-grade event management solutions. Transform your events into powerful growth engines.
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#00AAFF] transition-colors group">
                  <SafeIcon icon={FiFacebook} className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#00AAFF] transition-colors group">
                  <SafeIcon icon={FiTwitter} className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#00AAFF] transition-colors group">
                  <SafeIcon icon={FiLinkedin} className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#00AAFF] transition-colors group">
                  <SafeIcon icon={FiInstagram} className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-white">Platform</h3>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-slate-400 hover:text-[#00AAFF] transition-colors flex items-center space-x-2 group">
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>Home</span>
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-slate-400 hover:text-[#00AAFF] transition-colors flex items-center space-x-2 group">
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>About Us</span>
                  </a>
                </li>
                <li>
                  <a href="#events" className="text-slate-400 hover:text-[#00AAFF] transition-colors flex items-center space-x-2 group">
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>Events</span>
                  </a>
                </li>
                <li>
                  <Link to="/login" className="text-slate-400 hover:text-[#00AAFF] transition-colors flex items-center space-x-2 group">
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>Login</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-white">Contact</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-[#00AAFF] mt-0.5" />
                  <a href="mailto:info@prosperityleaders.com" className="text-slate-400 hover:text-white transition-colors">
                    info@prosperityleaders.com
                  </a>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <a href="tel:+1234567890" className="text-slate-400 hover:text-white transition-colors">
                    +1 (234) 567-890
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-slate-400 text-sm">
                &copy; {new Date().getFullYear()} Prosperity Leaders Events Platform. All rights reserved.
              </p>
              <div className="flex space-x-6 text-sm">
                <a href="#" className="text-slate-400 hover:text-[#00AAFF] transition-colors">Privacy Policy</a>
                <a href="#" className="text-slate-400 hover:text-[#00AAFF] transition-colors">Terms of Service</a>
                <a href="#" className="text-slate-400 hover:text-[#00AAFF] transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;